import { useEffect, useState } from 'react';
import { HbsInput } from './hbs/Input';
import { HbsButton } from './hbs/Button';
import { RecommendedBadge } from './hbs/RecommendedBadge';

interface Props {
  kind: 'web' | 'android-app';
  values: string[];
  onChange: (next: string[]) => void;
  /** Flag the field as recommended: badge by the legend, tinted rows. */
  recommended?: boolean;
}

interface RowProps {
  value: string;
  placeholder: string;
  ariaRemoveLabel: string;
  isWeb: boolean;
  onCommit: (next: string) => void;
  onRemove: () => void;
  showPackageError: boolean;
  recommended?: boolean;
}

const PACKAGE_NAME_RE = /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/;

function normalizeWeb(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  return withScheme.replace(/\/+$/, '');
}

function IdentifierRow({
  removeId,
  value,
  placeholder,
  ariaRemoveLabel,
  isWeb,
  onCommit,
  onRemove,
  showPackageError,
  recommended,
}: RowProps & { removeId: string }): JSX.Element {
  // Track the in-flight draft so onBlur can read what's typed even when the
  // parent hasn't re-rendered with a new `value` prop yet. onFocus re-syncs
  // from prop in case parent state moved while we were unfocused.
  const [draft, setDraft] = useState(value);

  // Reflect EXTERNAL value changes (e.g. the editor pre-filling the website
  // from the item name) into the visible input. During local typing this is a
  // no-op because onChange commits each keystroke to the parent, so `value`
  // already equals `draft`.
  useEffect(() => {
    setDraft(value);
  }, [value]);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <HbsInput
            value={draft}
            placeholder={placeholder}
            recommended={recommended}
            onChange={(v) => {
              setDraft(v);
              onCommit(v);
            }}
            onFocus={() => setDraft(value)}
            onBlur={() => {
              if (isWeb) {
                const normalized = normalizeWeb(draft);
                setDraft(normalized);
                onCommit(normalized);
              } else {
                const trimmed = draft.trim();
                if (trimmed !== draft) {
                  setDraft(trimmed);
                  onCommit(trimmed);
                }
              }
            }}
          />
        </div>
        <button
          id={removeId}
          type="button"
          aria-label={value ? `${ariaRemoveLabel}: ${value}` : ariaRemoveLabel}
          onClick={onRemove}
          className="shrink-0 p-1 rounded-hbs-sm text-hbs-text-mute hover:text-hbs-text hover:bg-hbs-surface-alt"
        >
          ×
        </button>
      </div>
      {showPackageError && (
        <span className="text-xs text-hbs-warning ml-1">
          Looks like a domain — paste that in Websites instead.
        </span>
      )}
    </div>
  );
}

export default function IdentifierListField({ kind, values, onChange, recommended }: Props): JSX.Element {
  const isWeb = kind === 'web';
  const heading = isWeb ? 'Websites' : 'Android apps';
  const addLabel = isWeb ? 'Add website' : 'Add app';
  const placeholder = isWeb ? 'https://example.com' : 'com.example.app';

  function updateAt(i: number, next: string): void {
    const copy = [...values];
    copy[i] = next;
    onChange(copy);
  }

  function removeAt(i: number): void {
    onChange(values.filter((_, j) => j !== i));
  }

  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="flex items-center gap-2 text-xs font-medium text-hbs-text-mute uppercase tracking-hbs-overline">
        <span>{heading}</span>
        {recommended && <RecommendedBadge />}
      </legend>
      {values.map((v, i) => {
        const showPackageError = !isWeb && v.length > 0 && !PACKAGE_NAME_RE.test(v.trim());
        return (
          <IdentifierRow
            key={i}
            removeId={`identifier-${kind}-remove-${i}`}
            value={v}
            placeholder={placeholder}
            ariaRemoveLabel={`Remove ${heading.toLowerCase()}`}
            isWeb={isWeb}
            onCommit={(next) => updateAt(i, next)}
            onRemove={() => removeAt(i)}
            showPackageError={showPackageError}
            recommended={recommended}
          />
        );
      })}
      <HbsButton
        id={`identifier-${kind}-add-button`}
        variant="ghost"
        type="button"
        onClick={() => onChange([...values, ''])}
      >
        + {addLabel}
      </HbsButton>
    </fieldset>
  );
}
